// frontend/src/components/auth/ChangePassword.jsx
import React, { useState } from 'react';
import { changePassword, auth, reauthenticateUser } from '../../firebase/config';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('You must be logged in to change your password');
      }
      
      // Reauthenticate the user first
      try {
        await reauthenticateUser(user, currentPassword);
      } catch (reauthError) {
        console.error('Reauthentication failed:', reauthError);
        if (reauthError.code === 'auth/wrong-password') {
          throw new Error('Current password is incorrect');
        } else {
          throw reauthError;
        }
      }
      
      // If reauthentication is successful, change the password
      await changePassword(user, newPassword);
      
      setMessage('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error changing password:', err);
      
      if (err.message === 'Current password is incorrect') {
        setError(err.message);
      } else if (err.code === 'auth/requires-recent-login') {
        setError('For security reasons, please log out and log in again before changing your password.');
      } else {
        setError('Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    // frontend/src/components/auth/ChangePassword.jsx (continued)
   <div className="bg-[#1e293b] rounded-xl shadow-xl p-6 border border-gray-700">
     <h3 className="text-xl font-semibold text-white mb-4">Change Password</h3>
     
     {message && (
       <div className="bg-green-800 border-l-4 border-green-500 p-4 mb-4">
         <p className="text-sm text-green-100">{message}</p>
       </div>
     )}
     
     {error && (
       <div className="bg-red-900 border-l-4 border-red-500 p-4 mb-4">
         <p className="text-sm text-red-100">{error}</p>
       </div>
     )}
     
     <form onSubmit={handleSubmit} className="space-y-4">
       <div>
         <label className="block text-sm font-medium text-gray-200">Current Password</label>
         <input
           type="password"
           required
           value={currentPassword}
           onChange={(e) => setCurrentPassword(e.target.value)}
           className="mt-1 block w-full rounded-md bg-[#273349] border border-gray-600 px-3 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
           placeholder="Enter current password"
         />
       </div>
       
       <div>
         <label className="block text-sm font-medium text-gray-200">New Password</label>
         <input
           type="password"
           required
           value={newPassword}
           onChange={(e) => setNewPassword(e.target.value)}
           className="mt-1 block w-full rounded-md bg-[#273349] border border-gray-600 px-3 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
           placeholder="Enter new password"
         />
       </div>
       
       <div>
         <label className="block text-sm font-medium text-gray-200">Confirm New Password</label>
         <input
           type="password"
           required
           value={confirmPassword}
           onChange={(e) => setConfirmPassword(e.target.value)}
           className="mt-1 block w-full rounded-md bg-[#273349] border border-gray-600 px-3 py-2 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
           placeholder="Confirm new password"
         />
       </div>
       
       <div>
         <button
           type="submit"
           disabled={loading}
           className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-500"
         >
           {loading ? 'Updating...' : 'Update Password'}
         </button>
       </div>
     </form>
   </div>
 );
};

export default ChangePassword;